import { t } from '@hefe/i18n';
import { Partner } from '@hefe/models';
import { formatDate } from '@hefe/utils';
import ClubCrudComponent from '../../../crud/components/club-crud/club-crud.component';
import ChildImageUploadCell from '../../../crud/components/image-upload-cell/child-image-upload-cell.component';
import ImageUploadCell from '../../../crud/components/image-upload-cell/image-upload-cell.component';
import {
  ChildColumnContext,
  ChildRelationConfig,
  CrudConfig,
  CrudEntity,
} from '../../../crud/models/club-crud.model';

/**
 * Partner Section interface
 * TODO: Once backend generates this, import from @hefe/models
 */
interface PartnerSection extends CrudEntity {
  id: number;
  partner_id: number;
  type: 'text' | 'coupon' | 'image' | 'link' | 'ad' | 'custom';
  title?: string;
  body?: string;
  image_path?: string;
  starts_at?: string;
  ends_at?: string;
  sort_order: number;
  is_active: boolean;
}

/**
 * Partner Sections Child Relation Configuration
 */
const sectionsConfig: ChildRelationConfig<PartnerSection> = {
  relation: 'sections',
  i18nKey: 'partners.sections',
  displayName: t('partners.sections.plural'),
  editTitleKey: 'title',
  multiDelete: true,
  columns: [], // Not used when getColumns is provided
  getColumns: (ctx: ChildColumnContext) => [
    {
      key: 'title',
      dataIndex: 'title',
      title: t('partners.sections.form.fields.title'),
      default: true,
      sortType: 'string',
    },
    {
      key: 'type',
      dataIndex: 'type',
      title: t('partners.sections.form.fields.type.title'),
      default: true,
      render: (value) => {
        return t(`partners.sections.form.fields.type.enum.${value}`);
      },
    },
    {
      key: 'sort_order',
      dataIndex: 'sort_order',
      title: t('partners.sections.form.fields.sort_order'),
      default: true,
      sortType: 'number',
    },
    {
      key: 'is_active',
      dataIndex: 'is_active',
      title: t('partners.sections.form.fields.active'),
      default: true,
      render: (value) => (value ? '✓' : '✗'),
    },
    {
      key: 'starts_at',
      dataIndex: 'starts_at',
      title: t('partners.sections.form.fields.starts_at'),
      default: true,
      sortType: 'date',
      render: (value) => formatDate(value),
    },
    {
      key: 'ends_at',
      dataIndex: 'ends_at',
      title: t('partners.sections.form.fields.ends_at'),
      default: true,
      sortType: 'date',
      render: (value) => formatDate(value),
    },
    {
      key: 'image_path',
      dataIndex: 'image_path',
      title: t('partners.sections.form.fields.image'),
      default: true,
      render: (value, record) => (
        <ChildImageUploadCell
          value={value}
          parentResource={ctx.parentResource}
          parentId={ctx.parentId}
          relation={ctx.relation}
          childId={record.id}
          fieldName="image_path"
        />
      ),
    },
  ],
  formFields: [
    {
      type: 'select',
      name: 'type',
      label: t('partners.sections.form.fields.type.title'),
      selectOptions: [
        {
          value: 'coupon',
          label: t('partners.sections.form.fields.type.enum.coupon'),
        },
        {
          value: 'news',
          label: t('partners.sections.form.fields.type.enum.news'),
        },
      ],
      rules: [{ required: true, message: t('form_errors.required') }],
    },
    {
      type: 'text',
      name: 'title',
      label: t('partners.sections.form.fields.title'),
      rules: [{ required: true, message: t('form_errors.required') }],
    },
    {
      type: 'textarea',
      name: 'body',
      label: t('partners.sections.form.fields.body'),
      rules: [],
    },
    {
      type: 'date',
      name: 'starts_at',
      label: t('partners.sections.form.fields.starts_at'),
      rules: [],
    },
    {
      type: 'date',
      name: 'ends_at',
      label: t('partners.sections.form.fields.ends_at'),
      rules: [],
    },
    {
      type: 'checkbox',
      name: 'is_active',
      label: t('partners.sections.form.fields.active'),
      rules: [],
    },
  ],
};

/**
 * Partners CRUD Configuration
 *
 * Uses standard CustomTableColumn and FormField types -
 * same structure as UsersComponent, but with automatic CRUD handling.
 */
const partnersConfig: CrudConfig<Partner> = {
  resource: 'partners',
  displayName: t('partners.singular'),
  editTitleKey: 'name',
  multiDelete: true,

  // Table columns - exactly like in UsersComponent
  columns: [
    {
      key: 'name',
      dataIndex: 'name',
      title: t('partners.form.fields.name'),
      default: true,
      sortType: 'string',
      showFilter: true,
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: t('partners.form.fields.description'),
      default: true,
    },
    {
      key: 'image',
      dataIndex: 'image',
      title: t('partners.form.fields.image'),
      default: true,
      render: (value, record) => (
        <ImageUploadCell
          value={value}
          resource="partners"
          itemId={record.id}
          fieldName="image"
        />
      ),
    },
    {
      key: 'category',
      dataIndex: 'category',
      title: t('partners.form.fields.category'),
      default: true,
    },
    {
      key: 'sort_order',
      dataIndex: 'sort_order',
      title: t('partners.form.fields.sort_order'),
      default: true,
      sortType: 'number',
    },
    {
      key: 'website',
      dataIndex: 'website',
      title: t('partners.form.fields.url'),
      default: true,
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : null,
    },
  ],

  // Form fields - exactly like in AddEditUserForm etc.
  formFields: [
    {
      type: 'text',
      name: 'name',
      label: t('partners.form.fields.name'),
      rules: [{ required: true, message: t('form_errors.required') }],
    },
    {
      type: 'textarea',
      name: 'description',
      label: t('partners.form.fields.description'),
      rules: [],
    },
    {
      type: 'text',
      name: 'website',
      label: t('partners.form.fields.url'),
      rules: [],
    },
    {
      type: 'text',
      name: 'street',
      label: t('partners.form.fields.street'),
      rules: [],
    },
    {
      type: 'text',
      name: 'city',
      label: t('partners.form.fields.city'),
      rules: [],
    },
    {
      type: 'text',
      name: 'postal_code',
      label: t('partners.form.fields.zip_code'),
      rules: [],
    },
    {
      type: 'text',
      name: 'category',
      label: t('partners.form.fields.category'),
      extraLabel: t('partners.form.info.category'),
      rules: [],
    },
    {
      type: 'number',
      name: 'sort_order',
      label: t('partners.form.fields.sort_order'),
      extraLabel: t('partners.form.info.sort_order'),
      rules: [
        {
          type: 'number',
          min: -100,
          message: t('form_errors.number_min', {
            min: -100,
          }),
        },
        {
          type: 'number',
          max: 100,
          message: t('form_errors.number_max', {
            max: 100,
          }),
        },
      ],
    },
  ],

  // Child relations - nested CRUD tables
  childRelations: [sectionsConfig],
};

export default function PartnersPage() {
  return <ClubCrudComponent<Partner> config={partnersConfig} />;
}
